
import React, { createContext, useContext, useState, useCallback } from 'react';

// SelectedLocation accepts various location shapes from different sources
// (PostLocationExtended, Location, raw location data). A proper union type
// would require significant refactoring across multiple components.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SelectedLocation = any | null;

type LocationModalContextType = {
    isOpen: boolean;
    currentRoute: string | null; // Track which route the modal should be open on
    setIsOpen: (value: boolean, route?: string | null) => void;
    selectedLocation: SelectedLocation;
    setSelectedLocation: (loc: SelectedLocation) => void;
};

const LocationModalContext = createContext<LocationModalContextType | undefined>(undefined);

export const LocationModalProvider = ({ children }: { children: React.ReactNode }) => {
    const [isOpen, setIsOpenState] = useState(false);
    const [currentRoute, setCurrentRoute] = useState<string | null>(null);
    const [selectedLocation, setSelectedLocation] = useState<SelectedLocation>(null);

    // Route-aware setIsOpen: only sets isOpen if route matches
    const setIsOpen = useCallback((value: boolean, route?: string | null) => {
        if (value) {
            // Opening modal: set route and open
            setCurrentRoute(route || null);
            setIsOpenState(true);
        } else {
            // Closing modal: clear route and close
            setCurrentRoute(null);
            setIsOpenState(false);
        }
    }, []);

    return (
        <LocationModalContext.Provider value={{ 
            isOpen, 
            currentRoute,
            setIsOpen, 
            selectedLocation, 
            setSelectedLocation,
        }}>
            {children}
        </LocationModalContext.Provider>
    );
};

export const useLocationModal = () => {
    const context = useContext(LocationModalContext);
    if (!context) throw new Error('useLocationModal must be used within a LocationModalProvider');
    return context;
};

export { LocationModalContext };

